// #356, Antenore 17/10: on a program a model wrote, the focus changes from the
// next block on, and the rounds done stay as they were. The block is added as
// flow 9 adds it (addNextBlock); then each exercise the model wrote takes the
// model's rows at the new focus in the new rounds only, and only when its
// entry changes with the focus; the others keep the copy, hand edits too. An
// exercise added by hand, swapped, or in a superset keeps the copy. A row the new focus adds
// comes in as a new exercise, off in the rounds before; a row it drops is
// off in the new rounds. The program's days meet the model's by name, not by
// place: the athlete may have moved, added or removed one. Keep free of React
// and any UI import.
import { deepEqual } from "./deepEqual.js";
import type { EffortScale } from "./effortScale.js";
import { SUPPORTED_LOCALES, type Locale } from "./locale.js";
import { pickLocalizedText, type LocalizedText } from "./localizedText.js";
import type { CellDraft, Draft, SessionDraft, SetRowDraft, SlotDraft } from "./programEdit.js";
import { kindAt } from "./programEditBuild.js";
import { MAX_DAY_EXERCISES } from "./programEditProblems.js";
import { absentCell } from "./programEditRoundSets.js";
import { namesOf } from "./programDayRole.js";
import { modelDayText, modelHasFocus, type ModelDay, type ModelRow, type ProgramModel } from "./programModel.js";
import { modelRowAt, modelRowExercise, modelRowSlot, type ModelChoices } from "./programModelDraft.js";
import { addNextBlock, type NextBlockResult } from "./programProgressionRounds.js";
import type { ProgramOrigin } from "./schemas/programOrigin.js";
import type { ProgramFocus, RoundType } from "./schemas/programV3.js";

type BlockOptions = NonNullable<Parameters<typeof addNextBlock>[1]> & {
  effortScale: EffortScale;
  exerciseName?: (slug: string) => string | undefined;
};

/** The sheet offers the focus: the program keeps its origin, the model has
 *  a focus, and its block is the block being added. */
export const modelBlockFits = (
  draft: Pick<Draft, "origin">,
  model: Omit<ProgramModel, "id">,
  types: readonly RoundType[],
): boolean =>
  draft.origin !== undefined &&
  modelHasFocus(model) &&
  model.roundPattern.length === types.length &&
  model.roundPattern.every((t, i) => t === types[i]);

/** The row has `exerciseId` at the program's level, at any focus. */
const rowHas = (row: ModelRow, origin: ProgramOrigin, exerciseId: string): boolean =>
  origin.picks[row.id] === exerciseId ||
  [row.byLevel[origin.level], ...Object.values(row.byFocus ?? {}).map((b) => b?.[origin.level])].some(
    (e) => e?.exercises.includes(exerciseId) === true,
  );

/** The row's entry is the same at both focuses: the model does not change it
 *  with the focus, so the block before is the one to carry on. */
const sameEntry = (row: ModelRow, origin: ProgramOrigin, was: ProgramFocus | undefined, focus: ProgramFocus | undefined): boolean => {
  const before = modelRowAt(row, origin.level, origin.minutes, was);
  const after = modelRowAt(row, origin.level, origin.minutes, focus);
  return before !== null && after !== null && deepEqual(before, after);
};

/** Cell `j` of `from`, written into `to`: its axis travels. */
const carry = (from: SetRowDraft, j: number, to: Pick<SetRowDraft, "intensityKind">): CellDraft => {
  const cell = from.cells[j];
  if (!cell || cell.absent) return absentCell();
  const out: CellDraft = { ...cell };
  delete out.kind;
  const kind = kindAt(from, j);
  return kind === to.intensityKind ? out : { ...out, kind };
};

/** `slot` with the model's rows of `built` in rounds n.. instead of the copy.
 *  null when a set is bound otherwise (reps against time): the copy stays. */
const withBlock = (slot: SlotDraft, built: SlotDraft, n: number): SlotDraft | null => {
  const count = Math.max(slot.setRows.length, built.setRows.length);
  const weeks = slot.inactiveWeeks.length;
  const setRows: SetRowDraft[] = [];
  for (let i = 0; i < count; i += 1) {
    const own = slot.setRows[i];
    const model = built.setRows[i];
    if (own && model && (own.workKind ?? "reps") !== (model.workKind ?? "reps")) return null;
    // A row only the model has: no notes, and no set in the rounds before.
    const { notesRawPerCell, ...head } = own ?? { ...model!, notesPerCell: Array.from({ length: weeks }, () => "") };
    setRows.push({
      ...head,
      ...(own && notesRawPerCell ? { notesRawPerCell } : {}),
      cells: Array.from({ length: weeks }, (_, w) =>
        w < n ? (own?.cells[w] ?? absentCell()) : model ? carry(model, w - n, head) : absentCell(),
      ),
    });
  }
  const inactiveWeeks = slot.inactiveWeeks.map((off, w) => (w < n ? off : (built.inactiveWeeks[w - n] ?? false)));
  return { ...slot, setRows, inactiveWeeks };
};

/** A row the new focus brings: off in the n rounds before, its cells there
 *  a copy of the block's so every round has the row's shape. */
const newSlot = (built: SlotDraft, n: number): SlotDraft => {
  const k = built.inactiveWeeks.length;
  const old = (w: number) => w % k;
  return {
    ...built,
    setRows: built.setRows.map((r) => ({
      ...r,
      cells: [...Array.from({ length: n }, (_, w) => ({ ...r.cells[old(w)]! })), ...r.cells],
      notesPerCell: Array.from({ length: n + k }, () => ""),
    })),
    inactiveWeeks: [...Array.from({ length: n }, () => true), ...built.inactiveWeeks],
  };
};

/** The new rounds keep the slot's exercise. One the athlete swapped in by
 *  hand stays when the entry at the new focus has it. Otherwise the exercise
 *  is the wizard's: when it is another one, the new rounds take it in a slot
 *  of their own, so the rounds done keep theirs. */
const keepsExercise = (
  slot: SlotDraft,
  row: ModelRow,
  origin: ProgramOrigin,
  was: ProgramFocus | undefined,
  focus: ProgramFocus | undefined,
  built: SlotDraft,
): boolean => {
  const before = modelRowAt(row, origin.level, origin.minutes, was);
  const after = modelRowAt(row, origin.level, origin.minutes, focus);
  const byHand = !before || slot.exerciseId !== modelRowExercise(before, origin.picks[row.id]);
  return byHand ? after?.exercises.includes(slot.exerciseId) === true : slot.exerciseId === built.exerciseId;
};

/** `slot` off in rounds n.. on. */
const offFrom = (slot: SlotDraft, n: number): SlotDraft => ({
  ...slot,
  inactiveWeeks: slot.inactiveWeeks.map((off, w) => (w < n ? off : true)),
});

/** One day: the slots matched to the model's rows, in the rows' order. */
const rebuildDay = (
  session: SessionDraft,
  day: ModelDay,
  origin: ProgramOrigin,
  was: ProgramFocus | undefined,
  choices: ModelChoices,
  types: readonly RoundType[],
  n: number,
): SlotDraft[] => {
  const slots = [...session.slots];
  const used = new Set<string>();
  let cursor = 0;
  for (const row of day.rows) {
    const built = modelRowSlot(row, choices, types);
    const at = slots.findIndex((s) => !used.has(s.id) && !s.group && rowHas(row, origin, s.exerciseId));
    if (at >= 0) {
      const slot = slots[at]!;
      used.add(slot.id);
      cursor = at + 1;
      if (!built) slots[at] = offFrom(slot, n);
      if (!built || sameEntry(row, origin, was, choices.focus)) continue;
      if (!keepsExercise(slot, row, origin, was, choices.focus, built) && slots.length < MAX_DAY_EXERCISES) {
        const added = newSlot(built, n);
        slots[at] = offFrom(slot, n);
        slots.splice(at + 1, 0, added);
        used.add(added.id);
        cursor = at + 2;
        continue;
      }
      const next = withBlock(slot, built, n);
      if (next) slots[at] = next;
      continue;
    }
    // Not in the program: the athlete took it out, unless the old focus
    // did not have it.
    if (!built || modelRowAt(row, origin.level, origin.minutes, was) || slots.length >= MAX_DAY_EXERCISES) continue;
    const added = newSlot(built, n);
    used.add(added.id);
    slots.splice(cursor, 0, added);
    cursor += 1;
  }
  return slots;
};

/** The texts of a name, every language or every entry, trimmed; empty ones
 *  left out. */
const textsOf = (text: LocalizedText | readonly string[] | undefined): string[] =>
  Object.values(text ?? {}).flatMap((v) => (typeof v === "string" && v.trim() ? [v.trim()] : []));

/** Each session's model day: the first day not taken yet with a name of the
 *  session's, in any language the model carries, at any focus. A session
 *  with none is undefined; a model day no session names is left out. */
const daysByName = (sessions: readonly SessionDraft[], days: readonly ModelDay[]): Array<ModelDay | undefined> => {
  const free = [...days];
  return sessions.map((session) => {
    const own = textsOf(namesOf(session));
    const i = free.findIndex((day) => {
      const names = new Set([day.name, ...Object.values(day.nameByFocus ?? {})].flatMap(textsOf));
      return own.some((name) => names.has(name));
    });
    return i < 0 ? undefined : free.splice(i, 1)[0];
  });
};

/** The day's name at the new focus, when it is still the model's at the old
 *  one in some language: «3 · Deload» becomes «3 · Volume», «3 · Scarico»
 *  becomes «3 · Volume» in Italian. A name typed by hand stays. */
const renamed = (session: SessionDraft, day: ModelDay, was: ProgramFocus | undefined, focus: ProgramFocus, lang: Draft["editLocale"]): SessionDraft => {
  const old = modelDayText(day, was);
  const name = session.name.trim();
  const order: Locale[] = [...(lang ? [lang] : []), ...SUPPORTED_LOCALES];
  const locale = order.find((l) => old[l]?.trim() === name);
  const oldNames = new Set(textsOf(old));
  if (!locale || !textsOf(namesOf(session)).every((n) => oldNames.has(n))) return session;
  const now = pickLocalizedText(modelDayText(day, focus), locale);
  if (now === session.name) return session;
  const out: SessionDraft = { ...session, name: now };
  delete out.nameRaw;
  return out;
};

/** Flow 9 at `focus`: the next block, its rows from `model` at that focus
 *  (the origin's level, minutes and picks). A program without an origin, or
 *  at that focus already, gets addNextBlock as it is. */
export const addModelBlock = (
  draft: Draft,
  model: Omit<ProgramModel, "id">,
  focus: ProgramFocus,
  opts: BlockOptions,
): NextBlockResult => {
  const base = addNextBlock(draft, opts);
  const origin = draft.origin;
  const types = opts.types ?? model.roundPattern;
  if (!base.ok || !origin || focus === draft.focus || !modelBlockFits(draft, model, types)) return base;
  const n = draft.cycleWeeks;
  const choices: ModelChoices = {
    name: draft.name,
    level: origin.level,
    focus,
    minutes: origin.minutes,
    sessionsPerWeek: draft.sessionsPerWeek,
    blocks: 1,
    exercisePicks: origin.picks,
    effortScale: opts.effortScale,
    ...(opts.exerciseName ? { exerciseName: opts.exerciseName } : {}),
  };
  const days = daysByName(base.draft.sessions, model.days);
  const sessions = base.draft.sessions.map((session, d) => {
    const day = days[d];
    if (!day) return session;
    const slots = rebuildDay(session, day, origin, draft.focus, choices, types, n);
    return { ...renamed(session, day, draft.focus, focus, draft.editLocale), slots };
  });
  return { ok: true, draft: { ...base.draft, focus, sessions } };
};
